import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, MessageSquare, CheckCircle2, ChevronDown, ChevronUp, UserCheck, ShieldCheck } from 'lucide-react'
import { botSettingsApi, type EscalationPreset } from '@/services/botSettingsApi'

const DEFAULT_ESCALATION_MESSAGE =
  'Te estoy conectando con uno de nuestros asesores de ventas para brindarte una atención más personalizada. _En breve te responderemos._'
const DEFAULT_MAX_RESPONSES_MESSAGE =
  'Parece que necesitas ayuda más especializada. Te conecto con un asesor comercial que podrá asistirte mejor.'
const DEFAULT_HANDOFF_RECIPIENT_LABEL = 'asesor de ventas'

const QUICK_ROLE_SUGGESTIONS = [
  'asesor de ventas',
  'ejecutivo comercial',
  'equipo de atención',
  'asesor de servicio',
]

const schema = z.object({
  maxBotResponses: z.number().min(1).max(50).optional(),
  handoffRecipientLabel: z.string().min(1, 'El cargo de derivación es requerido'),
  escalationMessage: z.string().min(1, 'El mensaje de derivación es requerido'),
  maxResponsesMessage: z.string().min(1, 'El mensaje alternativo es requerido'),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  maxBotResponses: 10,
  handoffRecipientLabel: DEFAULT_HANDOFF_RECIPIENT_LABEL,
  escalationMessage: DEFAULT_ESCALATION_MESSAGE,
  maxResponsesMessage: DEFAULT_MAX_RESPONSES_MESSAGE,
}

function toFormValues(
  values?: Partial<{
    maxBotResponses: number | null
    handoffRecipientLabel: string | null
    escalationMessage: string | null
    maxResponsesMessage: string | null
  }>,
): FormValues {
  return {
    maxBotResponses: values?.maxBotResponses ?? DEFAULT_VALUES.maxBotResponses,
    handoffRecipientLabel: values?.handoffRecipientLabel?.trim() || DEFAULT_VALUES.handoffRecipientLabel,
    escalationMessage: values?.escalationMessage?.trim() || DEFAULT_VALUES.escalationMessage,
    maxResponsesMessage: values?.maxResponsesMessage?.trim() || DEFAULT_VALUES.maxResponsesMessage,
  }
}

function matchesPreset(values: FormValues, preset: EscalationPreset): boolean {
  return (
    values.handoffRecipientLabel === preset.config.handoffRecipientLabel &&
    values.escalationMessage === preset.config.escalationMessage &&
    values.maxResponsesMessage === preset.config.maxResponsesMessage
  )
}

export default function EscalationSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [presets, setPresets] = useState<EscalationPreset[]>([])
  const [selectedPresetId, setSelectedPresetId] = useState<string>('ventas_suave')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema as any),
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    let mounted = true

    const loadSettings = async () => {
      try {
        setLoading(true)
        const data = await botSettingsApi.getSettings()
        if (!mounted) return

        setPresets(data.escalationPresets ?? [])
        setSelectedPresetId(data.escalationPresetId ?? data.escalationPresets?.[0]?.id ?? 'ventas_suave')
        reset(
          toFormValues({
            maxBotResponses: data.maxBotResponses ?? DEFAULT_VALUES.maxBotResponses,
            handoffRecipientLabel: data.handoffRecipientLabel ?? DEFAULT_VALUES.handoffRecipientLabel,
            escalationMessage: data.escalationMessage ?? DEFAULT_VALUES.escalationMessage,
            maxResponsesMessage: data.maxResponsesMessage ?? DEFAULT_VALUES.maxResponsesMessage,
          }),
        )
      } catch (error) {
        console.error('Error loading escalation settings:', error)
        toast.error('No se pudo cargar la configuración de derivación')
        setPresets([])
        setSelectedPresetId('ventas_suave')
        reset(DEFAULT_VALUES)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadSettings()

    return () => {
      mounted = false
    }
  }, [reset])

  const currentValues = watch()
  const activePreset = presets.find((preset) => matchesPreset(currentValues, preset)) ?? null

  const applyPreset = (preset: EscalationPreset) => {
    const currentMaxResponses = getValues('maxBotResponses') ?? DEFAULT_VALUES.maxBotResponses
    setSelectedPresetId(preset.id)
    reset({
      maxBotResponses: currentMaxResponses,
      handoffRecipientLabel: preset.config.handoffRecipientLabel,
      escalationMessage: preset.config.escalationMessage,
      maxResponsesMessage: preset.config.maxResponsesMessage,
    })
    toast.success(`Estilo de derivación aplicado: ${preset.label}`)
  }

  const onSubmit = async (values: FormValues) => {
    try {
      setSaving(true)
      await botSettingsApi.updateSettings({
        maxBotResponses: values.maxBotResponses,
        handoffRecipientLabel: values.handoffRecipientLabel.trim() || DEFAULT_HANDOFF_RECIPIENT_LABEL,
        escalationMessage: values.escalationMessage.trim(),
        maxResponsesMessage: values.maxResponsesMessage.trim(),
        escalationPresetId: selectedPresetId,
      })
      toast.success('Configuración de derivación guardada')
    } catch (err) {
      console.error('Error saving escalation settings:', err)
      toast.error('Error al guardar la configuración')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#075E54]" />
      </div>
    )
  }

  const escalationText = watch('escalationMessage') || DEFAULT_ESCALATION_MESSAGE

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4 sm:p-6">
      {/* Header explicativo */}
      <div className="p-4 rounded-2xl border border-orange-200/80 bg-gradient-to-r from-orange-50/80 to-amber-50/40">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <UserCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm sm:text-base">
              Derivación a Atención Humana
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Configura cómo y a quién se transfiere la conversación cuando tu cliente solicita hablar con una persona
              o cuando una consulta requiere atención especializada.
            </p>
          </div>
        </div>
      </div>

      {/* Tarjetas de estilo de derivación (1 Clic) */}
      <div data-tour="escalation-presets" className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-gray-900">Estilo del mensaje de traspaso</h4>
            <p className="text-xs text-gray-500">
              Elige cómo debe sonar el mensaje que recibe tu cliente al transferir la conversación.
            </p>
          </div>
          {activePreset && (
            <span className="hidden sm:inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold">
              Activo: {activePreset.label}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {presets.map((preset) => {
            const isSelected = selectedPresetId === preset.id || activePreset?.id === preset.id

            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`text-left rounded-2xl border-2 p-4 transition-all duration-200 shadow-sm ${
                  isSelected
                    ? 'border-[#075E54] bg-emerald-50/40 ring-1 ring-[#075E54] shadow-md'
                    : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h5 className="text-sm font-bold text-gray-900">{preset.label}</h5>
                  {isSelected ? (
                    <CheckCircle2 className="w-5 h-5 text-[#075E54] flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border border-gray-300 flex-shrink-0" />
                  )}
                </div>
                <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">{preset.description}</p>
                <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-1.5 text-[11px] font-medium text-emerald-800">
                  <span>{isSelected ? '✓ Estilo seleccionado' : 'Pulsa para aplicar'}</span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Cargo o destinatario */}
      <div className="space-y-2">
        <label className="block text-sm font-bold text-gray-900">
          ¿Quién atiende cuando se transfiere a un humano?
        </label>
        <p className="text-xs text-gray-500">
          Este nombre se mencionará en el mensaje para que el cliente sepa con quién hablará.
        </p>

        <div className="flex gap-2 flex-wrap mb-2">
          {QUICK_ROLE_SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => setValue('handoffRecipientLabel', suggestion)}
              className="text-xs rounded-lg px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
            >
              + {suggestion}
            </button>
          ))}
        </div>

        <input
          {...register('handoffRecipientLabel')}
          type="text"
          className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none transition bg-white"
          placeholder={DEFAULT_HANDOFF_RECIPIENT_LABEL}
        />
      </div>

      {/* Mensaje de derivación principal */}
      <div className="space-y-1.5">
        <label className="block text-sm font-bold text-gray-900">
          Mensaje de confirmación al cliente
        </label>
        <textarea
          {...register('escalationMessage')}
          rows={3}
          className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none transition bg-white"
          placeholder={DEFAULT_ESCALATION_MESSAGE}
        />
      </div>

      {/* Vista previa tipo WhatsApp */}
      <div className="rounded-2xl border border-gray-200 bg-slate-50/70 p-4 space-y-2.5">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
          <MessageSquare className="w-4 h-4 text-[#075E54]" />
          <span>Vista Previa: Mensaje recibido por el cliente</span>
        </div>

        <div className="max-w-md bg-[#E5DDD5]/40 p-4 rounded-2xl border border-gray-200">
          <div className="flex justify-start">
            <div className="bg-white text-gray-900 text-xs sm:text-sm rounded-2xl rounded-tl-none px-3.5 py-2.5 shadow-sm max-w-[90%] border border-gray-100">
              <p className="whitespace-pre-wrap">{escalationText}</p>
              <span className="text-[10px] text-gray-400 block text-right mt-1.5">10:45 AM ✓✓</span>
            </div>
          </div>
        </div>
      </div>

      {/* Regla de Seguridad Inteligente */}
      <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs sm:text-sm text-emerald-950">
          <p className="font-bold mb-0.5">Protección continua de compras</p>
          <p className="text-emerald-800 text-xs leading-relaxed">
            El asistente <strong>nunca interrumpirá ni transferirá</strong> una conversación mientras el cliente esté
            en proceso de compra, seleccionando productos o coordinando el pago. Solo transferirá si el cliente lo solicita expresamente.
          </p>
        </div>
      </div>

      {/* Opciones avanzadas colapsables */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-left text-xs font-semibold text-gray-600 hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <span>⚙️ Opciones de protección avanzada</span>
            <span className="text-gray-400 font-normal">(Límites y mensajes automáticos)</span>
          </div>
          {showAdvanced ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {showAdvanced && (
          <div className="p-4 sm:p-5 border-t border-gray-100 space-y-4 bg-gray-50/50">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Mensaje alternativo (por consultas reiteradas sin decisión)
              </label>
              <textarea
                {...register('maxResponsesMessage')}
                rows={2}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                placeholder={DEFAULT_MAX_RESPONSES_MESSAGE}
              />
            </div>

            <div>
              <label htmlFor="maxBotResponses" className="block text-xs font-semibold text-gray-700 mb-1">
                Límite de respuestas automáticas por conversación
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="maxBotResponses"
                  type="number"
                  min={1}
                  max={50}
                  {...register('maxBotResponses', { valueAsNumber: true })}
                  className="w-24 text-xs bg-white"
                />
                <span className="text-xs text-gray-500">respuestas (Recomendado: 10)</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Después de esta cantidad de respuestas, la conversación pasa a tu equipo. Se reinicia en cada conversación
                nueva y cuando tu equipo devuelve el chat.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Botón de guardado */}
      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={isSubmitting || saving}
          className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl px-6 py-2.5 font-medium shadow-sm"
        >
          {isSubmitting || saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </form>
  )
}
